import {Component, DOCUMENT, HostListener, Inject, NgZone, OnDestroy, OnInit, Renderer2, ChangeDetectionStrategy} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {Observable, Subject} from "rxjs";
import {AddHomescreenService} from "./pwa/add-homescreen.service";
import {DEFAULT_INTERRUPTSOURCES, Idle} from "@ng-idle/core";
import {GeneralRequestService} from "./services/general-request.service";
import {NotificationService} from "./services/notification.service";
import {NavigationEnd, Router, RouterModule, RouterOutlet} from "@angular/router";
import {filter, takeUntil} from "rxjs/operators";
import {DateAdapter} from "@angular/material/core";
import {environment} from "src/environments/environment";
import {NgClass} from "@angular/common";
import {AuthService} from "./services/auth.service";
import {ModalService} from "./services/modal.service";
import {PageTitleService} from "./services/page-title.service";
import {UrlService} from "./services/url.service";
import {AccessibilityService} from "./shared/accessibility/accessibility.service";
import {AppConfigService} from "./services/app-config.service";
import {FabButtonComponent} from "./shared/fab-button-service/fab-button.component";
import {SkipLinkComponent} from "./shared/skip-link/skip-link.component";
import {IconService} from "./services/icon.service";
import {EmailValidationService} from "./services/email-validation.service";
import {FooterComponent} from "./features/before-login/footer/footer.component";
import {PlatformHeaderComponent} from "./features/layout/platform-header/platform-header.component";
import {
  SystemNotificationsComponent
} from "./features/system-notifications/system-notifications/system-notifications.component";
import { BrowserTranslationBannerComponent } from "./shared/components/browser-translation-banner/browser-translation-banner.component";
import {DeviceDetectService} from "./services/device-detect.service";
import {SideNavComponent} from "./features/layout/side-nav/side-nav.component";
import {BottomNavComponent} from "./features/layout/bottom-nav/bottom-nav.component";
import {SessionStorageService} from "./services/session-storage.service";
import {PlatformService} from "./services/platform.service";
import {getPathWithoutQueryAndHash} from "./shared/utils/url.utils";
import type {Locale} from "date-fns";
import {enUS, et, ru} from "date-fns/locale";

const DATE_LOCALES: Record<string, Locale> = {et, en: enUS, ru};

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.css"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SkipLinkComponent,
    FabButtonComponent,
    RouterOutlet,
    RouterModule,
    NgClass,
    FooterComponent,
    PlatformHeaderComponent,
    BrowserTranslationBannerComponent,
    SystemNotificationsComponent,
    SideNavComponent,
    BottomNavComponent,
  ]
})
export class AppComponent implements OnInit, OnDestroy {

  readonly MAIN_PAGE_ROUTES = [ '/', '/paid'];
  readonly DEFAULT_LANG = "et";
  readonly LEGACY_LANG_CODE = 'ee';
  readonly UPDATED_KEY = 'Updated';
  readonly LANG_KEY = 'lang';

  sessionInitialized$!: Observable<boolean>;
  isNew!: boolean;
  showSkipLink!: boolean;
  title = "egp";
  idleState = "Not started.";
  fetchNewMessagesInterval!: ReturnType<typeof setInterval>;
  currentUrl!: string;
  isMainPage = false;
  isPaidServiceContext = false;
  isBeforeLoginRoute = false;
  showFooter = true;
  isMobile = false;
  private isSessionInitialized = false;

  private etLoaded = false;
  private destroy$ = new Subject<void>();

  constructor(
    public translate: TranslateService,
    public authService: AuthService,
    public addHomescreenService: AddHomescreenService,
    private idle: Idle,
    private modalService: ModalService,
    private accessibility: AccessibilityService,
    private generalRequestService: GeneralRequestService,
    private notificationService: NotificationService,
    private ngZone: NgZone,
    private router: Router,
    private renderer: Renderer2,
    private pageTitleService: PageTitleService,
    private urlService: UrlService,
    private dateAdapter: DateAdapter<Date>,
    private configService: AppConfigService,
    private iconService: IconService,
    private emailValidationService: EmailValidationService,
    private deviceDetect: DeviceDetectService,
    private sessionStorage: SessionStorageService,
    private platformService: PlatformService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.isMobile = this.deviceDetect.isMobile;
    this.initializeServices();
  }

  ngOnInit() {
    this.initializeApp();
    this.setupSubscriptions();
    this.setupIdleHandling();
    this.setupMessageFetching();

    // Initialize paid services context: use landing flow (platform) so paid users see paid theme everywhere
    this.currentUrl = this.router.url;
    this.isPaidServiceContext = this.platformService.isPaidByLandingFlow();
    this.isBeforeLoginRoute = this.isBeforeLogin(this.currentUrl);

    // Setup message fetching
    this.sessionInitialized$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoggedIn => {
        if (isLoggedIn && !this.isNew) {
          this.resetIdle();
          this.setUpMessageFetching();
        } else {
          clearInterval(this.fetchNewMessagesInterval);
          this.idle.stop();
        }
      });
  }

  private initializeServices(): void {
    this.iconService.registerIcons();
    this.configService.loadConfig();
    this.sessionInitialized$ = this.authService.isSessionInitialized$();
    this.sessionInitialized$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isInit) => {
        this.isSessionInitialized = isInit;
        /* Re-evaluate footer when login state changes (e.g. user logs in) */
        if (this.currentUrl) {
          this.showFooter = this.shouldShowFooter(this.currentUrl);
        }
      });
    this.pageTitleService.initialize();
  }

  private initializeApp(): void {
    this.preloadEstonianTranslations();
    this.setLang();
    this.checkIfUpdated();
    this.emailValidationService.subscribeToEmailValidation(); }

  private setupSubscriptions(): void {
    this.setupAccessibilitySubscription();
    this.setupAuthSubscription();
    this.setupRouterSubscription();
  }

  private setupAccessibilitySubscription(): void {
    this.accessibility.showSkipLink$
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => this.showSkipLink = value);
  }

  private setupAuthSubscription(): void {
    this.authService.isNew$
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => this.isNew = value);
  }

  private setupRouterSubscription(): void {
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .pipe(filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$))
      .subscribe((event: NavigationEnd) => {
        this.handleNavigationEnd(event);
      });
  }

  private handleNavigationEnd(event: NavigationEnd): void {
    this.urlService.setPreviousUrl(this.currentUrl);
    this.currentUrl = event.url;
    this.urlService.setCurrentUrl(this.currentUrl);
    this.isMainPage = this.MAIN_PAGE_ROUTES.includes(getPathWithoutQueryAndHash(this.currentUrl));
    this.isPaidServiceContext = this.platformService.isPaidByLandingFlow();
    this.isBeforeLoginRoute = this.isBeforeLogin(this.currentUrl);
    this.showFooter = this.shouldShowFooter(this.currentUrl);
    this.resetFocus();
  }

  private isBeforeLogin(url: string): boolean {
    const path = url.split("?")[0];

    // /login and its subroutes are considered part of the before-login flow (onboarding),
    // even when a temporary SessionContext exists.
    if (path.startsWith("/login")) {
      return true;
    }

    // Main routes (/, /paid) require auth – guard redirects to /welcome when not logged in.
    // Use before-login layout until redirect completes to avoid flash of wrong layout on reload.
    if ((path === "/" || path.startsWith("/paid")) && !this.isSessionInitialized) {
      return true;
    }

    // Routes that should use the before-login shell only when the user is NOT logged in.
    // After login, these same paths should use the authenticated layout.
    const beforeLoginOnlyWhenLoggedOut = [
      "/welcome",
      "/practitioners",
      "/paid-services",
      "/terms",
      "/privacy",
      "/video-guides",
      "/price-list",
      "/emo",
    ];

    if (beforeLoginOnlyWhenLoggedOut.some(prefix => path.startsWith(prefix))) {
      return !this.isSessionInitialized;
    }

    return false;
  }

  private shouldShowFooter(url: string): boolean {
    const path = url.split("?")[0];

    // New-user onboarding and post-login biometric enrollment must never show the public footer,
    // even if a temporary authenticated session exists.
    if (path.startsWith("/login/new-user") || path.startsWith("/login/biometrics-enroll")) {
      return false;
    }

    /* When logged in, always show footer (including on terms, privacy, etc.) */
    if (this.isSessionInitialized) {
      return true;
    }
    const isFooterHiddenBeforeLoginRoute =
      url.startsWith("/practitioners") ||
      url.startsWith("/login") ||
      url.startsWith("/terms") ||
      url.startsWith("/privacy") ||
      url.startsWith("/video-guides") ||
      url.startsWith("/price-list") ||
      url.startsWith("/emo");

    return !isFooterHiddenBeforeLoginRoute;
  }

  private setupIdleHandling(): void {
    this.idle.onIdleEnd
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.resetIdle());

    this.idle.onIdleStart
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.modalService.showIdleModal(this.idleState));

    this.idle.onTimeout
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.handleIdleTimeout());
  }

  private setupMessageFetching(): void {
    this.sessionInitialized$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoggedIn => {
        if (isLoggedIn && !this.isNew) {
          this.resetIdle(); this.setUpMessageFetching();
        } else {
          this.clearMessageInterval();
          this.idle.stop();
        }
      });
  }

  private handleIdleTimeout(): void {
    this.idle.stop();
    this.clearMessageInterval();
    this.authService.clearSession();
  }

  resetIdle() {
    this.idle.stop();
    this.idle.setIdle(environment.session.sessionLength);
    this.idle.setTimeout(environment.session.idleLength);
    // sessionLength sets a timeout period. after that, when <idleLength> seconds of inactivity have past, the user will be considered timed out.
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    this.idle.watch();
  }

  setUpMessageFetching() {
    this.generalRequestService.fetchMeetings();
    this.notificationService.fetchNotificationsOnInit();
    this.ngZone.runOutsideAngular(() => {
      this.fetchNewMessagesInterval = setInterval(() => {
        this.ngZone.run(() => {
          this.generalRequestService.fetchMeetings(true);
          this.notificationService.fetchNewNotifications(true);
        });
      }, (environment.session.messageFetchFrequency * 1000));
    });
  }

  resetFocus() {
    // Set focus to an element at the top of the page for accessibility
    const topElement = this.renderer.selectRootElement("#top-of-page");
    if (topElement) {
      topElement.focus();
    }
  }


  /**
   * Preloads Estonian translations into NGX-Translate memory
   * without switching the active UI language.
   * Why this is needed:
   * - The user may use the app in EN or RU.
   * - However, backend requires saving certain values in Estonian.
   * - NGX-Translate only loads the currently active language automatically.
   * - We therefore manually load ET once at app startup.
   */
  private preloadEstonianTranslations(): void {
    if (this.etLoaded) return;
    this.etLoaded = true;
    import('../assets/i18n/et.json').then(module => {
      const etData = module.default || module;
      this.translate.setTranslation('et', etData, true);
    });
  }

  setLang() {
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(changeEvent => {
        localStorage.setItem(this.LANG_KEY, changeEvent.lang);
        sessionStorage.setItem(this.LANG_KEY, changeEvent.lang);
        this.dateAdapter.setLocale(DATE_LOCALES[changeEvent.lang] ?? et);
        this.document.documentElement.lang = changeEvent.lang;
      });

    const storedLang = localStorage.getItem(this.LANG_KEY);

    const selectedLang =
      storedLang === this.LEGACY_LANG_CODE
        ? this.DEFAULT_LANG
        : storedLang ?? this.DEFAULT_LANG;

    this.translate.use(selectedLang);
  }

  private checkIfUpdated() {
    const updated = localStorage.getItem(this.UPDATED_KEY);
    if (updated === 'true') {
      this.modalService.showNewVersionDialog('shared.versionUpdate');
      localStorage.removeItem(this.UPDATED_KEY);
    }
  }

  private clearMessageInterval() {
    clearInterval(this.fetchNewMessagesInterval);
  }

  ngOnDestroy() {
    this.clearMessageInterval();
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener("window:beforeinstallprompt", ["$event"])
  onbeforeinstallprompt(e: Event) {
    // Prevent Chrome 67 and earlier from automatically showing the prompt
    e.preventDefault();
    // Stash the event so it can be triggered later.
    this.addHomescreenService.storePrompt(e);
  }
}
